import { BackgroundImage } from "@/components/Primitives/BackgroundImage";
import dynamic from "next/dynamic";
import { sanitizeHTML } from "@/lib/utils";

const AnimatedSection = dynamic(
  () => import("../../components/Animations/useAnimations"),
  { ssr: false }
);
const ScreenAwareContent = dynamic(
  () => import("../../utils/screenAwareContent"),
  {
    ssr: false,
    loading: () => <div className="min-h-screen h-screen" />,
  }
);

interface YourRhinoplastyExpertData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
}

export const fields = [
  "heading.text",
  "heading.title",

  "subheading.text",
  "subheading.title",

  "text.text",

  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
] as const;

interface YourRhinoplastyExpertProps {
  zIndex: number;
  thresholds: number[][];
  mobileThresholds: number[][];
  transitionTime?: number;
  helperRotation?: number;
  data?: YourRhinoplastyExpertData;
}

export default function YourRhinoplastyExpert({
  zIndex,
  thresholds,
  mobileThresholds,
  transitionTime,
  helperRotation,
  data,
}: YourRhinoplastyExpertProps) {
  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full flex items-center bg-white pt-16 lg:pt-24 xl:pt-32 top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { y: 0, x: 0, opacity: 1 },
              above: { y: 0, x: 0, opacity: 0 },
              below: { y: 0, x: "-100vw", opacity: 0 },
            },
            mobile: {
              visible: { y: 0, x: 0, opacity: 1 },
              above: { y: "100vh", x: 0, opacity: 1 },
              below: { y: 0, x: "-100vw", opacity: 1 },
            },
          }}
        >
          <div className="gl-container relative gl-desktop-scale h-full w-full">
            <div className="lg:flex lg:gap-16 lg:items-center lg:justify-center h-full w-full">
              <div className="lg:w-[60%] relative h-full lg:h-auto w-full z-20 lg:flex lg:flex-col lg:justify-center">
                <AnimatedSection
                  section="div"
                  thresholds={[
                    thresholds?.[0]?.[0],
                    thresholds?.[thresholds.length - 1]?.[1],
                  ]}
                  zIndex={20}
                  withZIndex={true}
                  className="relative z-20 "
                  transitionTime={transitionTime}
                  variants={{
                    desktop: {
                      visible: { opacity: 1, x: 0 },
                      above: { opacity: 1, x: "-100vw" },
                      below: { opacity: 1, x: 0 },
                    },
                  }}
                >
                  <AnimatedSection
                    section="div"
                    mobileThresholds={[
                      mobileThresholds?.[0]?.[0],
                      mobileThresholds?.[mobileThresholds.length - 1]?.[1],
                    ]}
                    className="flex items-center justify-center h-full lg:h-auto lg:pt-11 lg:-mt-12 lg:text-right w-auto mt-[10vh]"
                    transitionTime={transitionTime}
                    variants={{
                      desktop: {
                        visible: { opacity: 1, y: 0, x: 0 },
                        above: { opacity: 1, y: 0, x: 0 },
                        below: { opacity: 1, y: 0, x: 0 },
                      },
                      mobile: {
                        visible: { opacity: 1, y: 0, x: 0 },
                        above: { opacity: 1, y: "100vh", x: 0 },
                        below: { opacity: 1, y: 0, x: 0 },
                      },
                    }}
                  >
                    <AnimatedSection
                      section="div"
                      transitionTime={transitionTime}
                      mobileThresholds={[
                        mobileThresholds?.[0]?.[0],
                        mobileThresholds?.[mobileThresholds.length - 1]?.[1],
                      ]}
                      variants={{
                        desktop: {
                          visible: { opacity: 1, color: "rgb(25, 55, 98)" },
                          above: { opacity: 1, color: "rgb(25, 55, 98)" },
                          below: { opacity: 1, color: "rgb(25, 55, 98)" },
                        },
                        mobile: {
                          visible: { opacity: 1, color: "rgb(255, 255, 255)" },
                          above: { opacity: 1, color: "rgb(255, 255, 255)" },
                          below: { opacity: 1, color: "rgb(255, 255, 255)" },
                        },
                      }}
                    >
                      <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem]">
                        {data?.heading?.title}
                      </h2>
                      <p className="mt-3 font-greycliff text-base font-light tracking-tight lg:mt-6 lg:text-3xl">
                        {data?.subheading?.title}
                      </p>
                    </AnimatedSection>
                  </AnimatedSection>
                </AnimatedSection>
                <div className="relative h-full w-full z-10">
                  <AnimatedSection
                    section="div"
                    thresholds={[
                      thresholds?.[0]?.[0],
                      thresholds?.[thresholds.length - 1]?.[1],
                    ]}
                    className="relative h-full w-full lg:h-[500px] scale-[1.4] -translate-y-[50px] lg:scale-100 lg:translate-y-0"
                    transitionTime={transitionTime}
                    variants={{
                      desktop: {
                        visible: { opacity: 1, x: 0 },
                        above: { opacity: 1, x: "-100vw" },
                        below: { opacity: 1, x: 0 },
                      },
                    }}
                  >
                    <BackgroundImage
                      assetId={data?.image?.image_file?.image?.id || ""}
                      className="absolute left-0 top-0 inset-0 lg:relative lg:rounded-2xl object-cover z-10"
                    />
                    <AnimatedSection
                      section="div"
                      mobileThresholds={[
                        mobileThresholds?.[0]?.[0],
                        mobileThresholds?.[mobileThresholds.length - 1]?.[1],
                      ]}
                      transitionTime={transitionTime}
                      zIndex={19}
                      withZIndex={true}
                      className="absolute left-0 top-0 w-full h-full inset-0 lg:hidden bg-primary/50 z-20"
                      variants={{
                        desktop: {
                          visible: { opacity: 1, y: 0, x: 0 },
                          above: { opacity: 1, y: 0, x: 0 },
                          below: { opacity: 1, y: 0, x: 0 },
                        },
                        mobile: {
                          visible: { opacity: 1, y: 0, x: 0 },
                          above: { opacity: 1, y: "100vh", x: 0 },
                          below: { opacity: 1, y: 0, x: 0 },
                        },
                      }}
                    >
                      <div className="absolute left-0 top-0 inset-0 lg:hidden bg-primary/50" />
                    </AnimatedSection>
                  </AnimatedSection>
                </div>
              </div>
              <AnimatedSection
                mobileThresholds={[
                  mobileThresholds?.[0]?.[0],
                  mobileThresholds?.[mobileThresholds.length - 1]?.[1],
                ]}
                className="flex items-center justify-center absolute inset-0 lg:pt-8 px-7 md:px-8 lg:px-0 lg:pr-20 left-0 lg:static lg:top-32 lg:flex lg:flex-col lg:justify-center text-[1rem] font-normal leading-normal lg:text-[1.25rem] font-greycliff tracking-tight text-white lg:max-w-[40%] lg:text-primary z-20 lg:z-10"
                transitionTime={transitionTime}
                zIndex={19}
                withZIndex={true}
                section="div"
                variants={{
                  desktop: {
                    visible: { opacity: 1, y: 0, x: 0 },
                    above: { opacity: 1, y: 0, x: 0 },
                    below: { opacity: 1, y: 0, x: 0 },
                  },
                  mobile: {
                    visible: { opacity: 1, y: 0, x: 0 },
                    above: { opacity: 1, y: "100vh", x: 0 },
                    below: { opacity: 1, y: 0, x: 0 },
                  },
                }}
              >
                <div
                  className="[&>p]:py-2 [&>*]:!text-[1rem] lg:[&>*]:!text-[1.25rem] [&>*]:!font-light [&>*]:!leading-normal max-[400px]:[&>p]:mt-8"
                  dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                />
              </AnimatedSection>
            </div>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full flex items-center bg-white pt-8 lg:pt-16">
          <div className="gl-container relative gl-desktop-scale h-full w-full">
            <div className="xl:flex xl:gap-16 xl:items-center xl:justify-center h-full w-full">
              <div className="xl:w-[60%] relative h-full xl:h-auto w-full z-20 xl:flex xl:flex-col xl:justify-center">
                <div className="relative z-20 ">
                  <div className="flex items-center justify-center h-full xl:h-auto xl:pt-11 xl:-mt-12 xl:text-right w-auto ">
                    <div>
                      <h2 className="text-[2rem] font-essonnes text-primary font-light leading-tight md:text-5xl lg:text-[3.375rem]">
                        {data?.heading?.title}
                      </h2>
                      <p className="mt-3 font-greycliff text-primary text-base font-light tracking-tight xl:mt-6 xl:text-3xl">
                        {data?.subheading?.title}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="relative h-full w-full z-10">
                  <div className="relative h-full w-full xl:h-[500px] scale-[1.4] -translate-y-[50px] xl:scale-100 xl:translate-y-0">
                    <BackgroundImage
                      assetId={data?.image?.image_file?.image?.id || ""}
                      className="absolute left-0 top-0 inset-0 xl:relative xl:rounded-2xl object-cover z-10"
                    />
                    <div className="absolute left-0 top-0 w-full h-full inset-0 xl:hidden bg-primary/50 z-20">
                      <div className="absolute left-0 top-0 inset-0 xl:hidden bg-primary/50" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center lg:block xl:pt-8 left-0 xl:static inset-0 xl:flex xl:flex-col xl:justify-center text-[1rem] font-normal leading-normal xl:text-[1.25rem] font-greycliff tracking-tight xl:max-w-[40%] text-primary z-20 xl:z-10">
                <div
                  className="[&>p]:py-2 [&>*]:!text-[1rem] xl:[&>*]:!text-[1.25rem] [&>*]:!font-light [&>*]:!leading-normal"
                  dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                />
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
